import React, {useState} from 'react'

import {Box, Text, useWindowSize, Portal, Desktop} from '@miro-site/core'

import type {LazyImageProps} from '@miro-site/core'

import styled, {useTheme} from 'styled-components'
import CardDescription from './components/CardDescription'
import CardImage from './components/CardImage'
import ActionsOverlay, {ActionsOverlayProps} from './components/ActionsOverlay'
import type {TemplateActions} from './templateActions'

export interface TemplateCardProps {
  appSlug: string
  templatePagePath: string
  className?: string
  title: string
  tags?: string[] | null
  description: string
  metadataId: string
  newBoardMetadataId?: string
  position?: number
  templateImage: LazyImageProps
  templateActions: TemplateActions
  previewButton?: (defaultButton: React.ReactElement) => React.ReactElement
  onMouseEnter?: React.MouseEventHandler<HTMLElement>
  onPreviewClick?: React.MouseEventHandler<HTMLElement>
  actionButtonProps: ActionsOverlayProps['actionButtonProps']
  accessConstraints?: {
    requiredPlans?: string[]
  }
}

const ImageContainer = styled(Box)`
  position: relative;
`

const TemplateCard: React.FC<TemplateCardProps> = ({
  metadataId,
  templateActions,
  templatePagePath,
  className,
  title,
  description,
  position,
  templateImage,
  onMouseEnter,
  previewButton,
  actionButtonProps,
  accessConstraints,
  appSlug,
  tags,
  // Avoid passing these props to <Box />
  metadataId: _metadataId,
  newBoardMetadataId: _newBoardMetadataId,
  ...props
}) => {
  const [showDescription, setShowDescription] = useState(false)

  const theme = useTheme()

  const windowSize = useWindowSize()

  const isMobileViewPort = windowSize.width && windowSize.width < theme.breakpoints.small

  const [isOverlayVisible, setOverlayVisible] = useState(false)

  const getContainerHandlers = () => {
    if (isMobileViewPort) {
      return {
        onClick: () => {
          setOverlayVisible(true)
        },
      }
    }

    return {
      onMouseEnter: () => setOverlayVisible(true),
      onMouseLeave: () => setOverlayVisible(false),
    }
  }

  return (
    <Box className={className} {...props} onMouseEnter={onMouseEnter}>
      <ImageContainer {...getContainerHandlers()}>
        <CardImage
          onShowCardDescription={() => setShowDescription(true)}
          image={{...templateImage, width: 360, height: 232}}
          priority={position === 0}
          wrapperStyles={{
            marginBottom: [12, 16],
            borderRadius: [8, 16],
            position: 'relative',
            width: '100%',
            overflow: 'hidden',
            height: 0,
            paddingTop: ['100%', '64.444%'],
            background: 'grey',
          }}
        />

        <Desktop $block={true}>
          <ActionsOverlay
            metadataId={metadataId}
            templateActions={templateActions}
            templatePagePath={templatePagePath}
            previewButton={previewButton}
            actionButtonProps={actionButtonProps}
            onMoreInfoClick={() => setShowDescription(true)}
            isVisible={isOverlayVisible}
          />
        </Desktop>
        {isMobileViewPort ? (
          <Portal>
            <CardDescription
              metadataId={metadataId}
              templateActions={templateActions}
              templatePagePath={templatePagePath}
              isShown={showDescription}
              isFullscreen={true}
              previewButton={previewButton}
              actionButtonProps={actionButtonProps}
              title={title}
              tags={tags}
              description={description}
              thumbnail={templateImage}
              onClose={() => setShowDescription(false)}
              popupStyles={{
                position: 'fixed',
                top: 0,
                right: 0,
                left: 0,
                height: '100%',
                overflow: 'auto',
                zIndex: 701,
                paddingTop: [64, null],
                paddingRight: [20, null],
                paddingBottom: [43, null],
                paddingLeft: [20, null],
              }}
            />
          </Portal>
        ) : (
          <CardDescription
            metadataId={metadataId}
            templateActions={templateActions}
            templatePagePath={templatePagePath}
            isShown={showDescription}
            previewButton={previewButton}
            actionButtonProps={actionButtonProps}
            title={title}
            tags={tags}
            description={description}
            thumbnail={templateImage}
            onClose={() => setShowDescription(false)}
            popupStyles={{
              position: 'absolute',
              overflow: 'hidden',
              width: '100%',
              minHeight: '100%',
              paddingTop: [null, 28],
              paddingRight: [null, 28],
              paddingBottom: [null, 28],
              paddingLeft: [null, 28],
              top: 0,
              left: 0,
            }}
          />
        )}
      </ImageContainer>
      <Text
        as="div"
        $styles={{
          fontSize: [14, 18],
          lineHeight: [18, 24],
          fontWeight: 700,
        }}
      >
        {title}
      </Text>
    </Box>
  )
}

export default TemplateCard
